import useDeleteOperation from 'api/operation/common/useDeleteOperation';
import useDepartureOperationList from 'api/operation/departure/useDepartureOperationList';
import useInsertDepartureOperation from 'api/operation/departure/useInsertDepartureOperation';
import useUpdateDepartureOperation from 'api/operation/departure/useUpdateDepartureOperation';
import DepartureOperationInsertModal from 'components/private/Modals/operation/departure/DepartureOperationInsertModal/DepartureOperationInsertModal';
import OperationEditModal from 'components/private/Modals/operation/OperationEditModal';
import DeleteModal from 'components/shared/DeleteModal/DeleteModal';
import PageSpinner from 'components/shared/pageSpinner/PageSpinner';
import RequireOperationSettings from 'components/shared/RequiredOpertaionSettings/RequireOperationSettings';
import ApiPaginateTable from 'components/shared/Table/ApiPaginateTable';
import useDepartureOperationTableColumns from 'hooks/operation/useDepartureOperationTableColumns';
import { permissions } from 'hooks/permissions/permissions';
import { usePermissions } from 'hooks/permissions/usePermissions';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';

const DepartureOperationList = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [{ operationSettings }] = useCookies(['operationSettings']);
  const zoneGuid = useMemo(() => {
    return operationSettings?.defaultZone?.key;
  }, [operationSettings?.defaultZone?.key]);

  const departurePermissions =
    permissions.general.categories.warehouseOperations.departure;

  const canCreate = can(departurePermissions.create);
  const canDelete = can(departurePermissions.delete);
  const canDetail = can(departurePermissions.detail);
  const canEdit = can(departurePermissions.edit);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('100');

  const {
    getDepartureOperationList,
    loading,
    departureOperationList,
    pageRef,
  } = useDepartureOperationList();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInsertModalOpen, setIsInsertModalOpen] = useState(false);
  const [deleteChosenDepartureOperation, setDeleteChosenDepartureOperation] =
    useState({});
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [insertLoading, setInsertLoading] = useState(false);
  const [departureOperationEditValues, setDepartureOperationEditValues] =
    useState({});

  const { deleteRequest } = useDeleteOperation();
  const { updateRequest } = useUpdateDepartureOperation();
  const { insertRequest } = useInsertDepartureOperation();

  const tableRef = useRef(null);

  const fetchList = useCallback(
    ({ page: pageNumber = 1, count = pageSize, term = '' } = {}) => {
      if (!zoneGuid) return;
      getDepartureOperationList({
        Guid: zoneGuid,
        Page: pageNumber,
        Count: count,
        Term: term,
      });
    },
    [getDepartureOperationList, pageSize, zoneGuid],
  );

  useEffect(() => {
    fetchList({ page: 1, count: pageSize });
  }, []);

  useEffect(() => {
    fetchList({ page, count: pageSize });
  }, [pageSize]);

  const deleteItem = useCallback(
    row => () => {
      setDeleteChosenDepartureOperation(row);
      setIsDeleteModalOpen(true);
    },
    [],
  );

  const editItem = useCallback(
    row => () => {
      setDepartureOperationEditValues(row);
      setIsEditModalOpen(true);
    },
    [],
  );

  const openItem = useCallback(
    row => () => {
      navigate(`/operation/departure-workspace/${row.guid}`);
    },
    [navigate],
  );

  const [columnsData] = useDepartureOperationTableColumns(
    deleteItem,
    editItem,
    openItem,
    tableRef,
    {
      canDetail,
      canEdit,
      canDelete,
    },
  );

  const deleteHandle = guid => {
    setDeleteLoading(true);
    deleteRequest(
      guid,
      () => fetchList({ page, count: pageSize }),
      setIsDeleteModalOpen,
      setDeleteLoading,
    );
  };

  const editHandle = data => {
    if (!data) return;

    setEditLoading(true);
    updateRequest(
      {
        guid: departureOperationEditValues.guid,
        title: data.title,
      },
      () => fetchList({ page, count: pageSize }),
      setIsEditModalOpen,
      setEditLoading,
    );
  };

  const insertHandle = payload => {
    setInsertLoading(true);
    insertRequest(
      () => fetchList({ page, count: pageSize }),
      setIsInsertModalOpen,
      setInsertLoading,
      payload,
    );
  };

  const closeDeleteModal = () => setIsDeleteModalOpen(false);
  const closeEditModal = () => setIsEditModalOpen(false);

  function getPageData(pageNumber) {
    setPage(pageNumber);
    fetchList({ page: pageNumber, count: pageSize });
  }

  function handleSearch(text) {
    setPage(1);
    fetchList({ page: 1, count: pageSize, term: text });
  }

  return (
    <RequireOperationSettings>
      <div>{pageRef.refTitle}</div>

      {loading ? (
        <PageSpinner loading />
      ) : (
        <ApiPaginateTable
          columns={columnsData}
          rowsData={departureOperationList?.[page] ?? []}
          rows={departureOperationList?.[page] ?? []}
          pageSize={pageSize}
          isDeletable={canDelete}
          isLoading={loading}
          addLable={canCreate ? 'عملیات جمع‌آوری جدید' : undefined}
          onAddClick={canCreate ? () => setIsInsertModalOpen(true) : undefined}
          setPageSize={setPageSize}
          apiRef={tableRef}
          onPageChange={getPageData}
          page={page}
          countPages={
            pageRef?.totalCount
              ? Math.ceil(pageRef.totalCount / Number(pageSize))
              : 1
          }
          handleSearchApi={handleSearch}
          disableSelection
        />
      )}

      {canDelete ? (
        <DeleteModal
          open={isDeleteModalOpen}
          handleClose={closeDeleteModal}
          locationId={deleteChosenDepartureOperation.guid}
          title={deleteChosenDepartureOperation.title}
          deleteLoading={deleteLoading}
          onDelete={deleteHandle}
        />
      ) : null}

      {canEdit ? (
        <OperationEditModal
          open={isEditModalOpen}
          handleClose={closeEditModal}
          editLoading={editLoading}
          onEdit={editHandle}
          initialValues={departureOperationEditValues}
        />
      ) : null}

      {canCreate ? (
        <DepartureOperationInsertModal
          open={isInsertModalOpen}
          handleClose={() => setIsInsertModalOpen(false)}
          insertLoading={insertLoading}
          onInsert={insertHandle}
        />
      ) : null}
    </RequireOperationSettings>
  );
};

export default DepartureOperationList;
